import type { DeliveryGroupDraft, DeliveryGroupStopDraft, StopExecutionLockState } from './deliveryGroupTypes';
import { formatGroupTitle } from './routeUxLabels';
import { getOrderedStops, type OrderedStopEntry } from './editorStopOrdering';
import { buildLocationLine, formatPointTypeRu } from './executionStopOptionLabels';
import {
  countCargoItemsAtStop,
  resolveItineraryAllocationEdges,
  type ItineraryCargoContext
} from './itineraryStopSummary';

export type OrderedRouteStopRow = {
  stopUid: string;
  position: number;
  type: 'loading' | 'unloading';
  stopName: string;
  typeLabel: string;
  pointTypeLabel: string;
  groupUid: string;
  groupLabel: string;
  applicationLabel: string | null;
  pointLabel: string | null;
  address: string | null;
  fullAddress: string | null;
  plannedAt: string | null;
  lockState: StopExecutionLockState;
  locked: boolean;
  cargoCount: number;
};

type ApplicationRef = {
  id: number;
  internalNumber?: string | null;
  client?: { name?: string | null } | null;
};

type WarehouseRef = {
  id: number;
  name?: string;
  city?: string;
  address?: string;
};

type ConsigneeRef = {
  id: number;
  name?: string;
  city?: string;
  address?: string;
};

function resolveLockState(stop: DeliveryGroupStopDraft): StopExecutionLockState {
  return stop.lockState ?? 'FUTURE';
}

function resolvePointLabel(
  stop: DeliveryGroupStopDraft,
  app: ApplicationRef | undefined,
  warehouses: WarehouseRef[],
  consignees: ConsigneeRef[]
): string | null {
  const pt = String(stop.pointType || '').toLowerCase();
  if (pt === 'warehouse') {
    const wh = warehouses.find((w) => w.id === Number(stop.pointId));
    return wh ? wh.name || `Склад #${wh.id}` : 'Склад';
  }
  if (pt === 'consignee') {
    const c = consignees.find((x) => x.id === Number(stop.pointId));
    return c ? c.name || `Получатель #${c.id}` : 'Получатель';
  }
  if (pt === 'application') {
    if (app?.internalNumber) return `Заявка №${app.internalNumber}`;
    if (stop.pointId) return `Заявка #${stop.pointId}`;
    return 'Заявка';
  }
  return null;
}

function entryToRow(
  entry: OrderedStopEntry,
  groups: DeliveryGroupDraft[],
  applications: ApplicationRef[],
  warehouses: WarehouseRef[],
  consignees: ConsigneeRef[],
  allocationEdges: ReturnType<typeof resolveItineraryAllocationEdges>,
  typeCounters: { loading: number; unloading: number }
): OrderedRouteStopRow {
  const groupIndex = groups.findIndex((g) => g.uid === entry.groupUid);
  const group = groups[groupIndex];
  const app = group?.applicationId
    ? applications.find((a) => a.id === group.applicationId)
    : undefined;
  const lockState = resolveLockState(entry.stop);
  const typeLabel = entry.stop.type === 'loading' ? 'Погрузка' : 'Выгрузка';
  const typeIndex =
    entry.stop.type === 'loading' ? ++typeCounters.loading : ++typeCounters.unloading;

  return {
    stopUid: entry.stop.uid,
    position: Number(entry.stop.position ?? 0),
    type: entry.stop.type,
    stopName: `${typeLabel} ${typeIndex}`,
    typeLabel,
    pointTypeLabel: formatPointTypeRu(entry.stop.pointType),
    groupUid: entry.groupUid,
    groupLabel: group
      ? formatGroupTitle({
          applicationNumber: app?.internalNumber ?? null,
          clientName: app?.client?.name ?? null,
          index: groupIndex >= 0 ? groupIndex : 0
        })
      : 'Группа',
    applicationLabel: app?.internalNumber ? `№${app.internalNumber}` : null,
    pointLabel: resolvePointLabel(entry.stop, app, warehouses, consignees),
    address: entry.stop.address?.trim() || null,
    fullAddress: buildLocationLine(entry.stop) || entry.stop.address?.trim() || null,
    plannedAt: entry.stop.plannedAt ?? null,
    lockState,
    locked: lockState !== 'FUTURE',
    cargoCount: countCargoItemsAtStop(entry.stop.uid, entry.stop.type, allocationEdges)
  };
}

/**
 * Global itinerary view model — sorted strictly by draft.position (not bucket order).
 */
export function buildOrderedRouteStops(params: {
  groups: DeliveryGroupDraft[];
  applications?: ApplicationRef[];
  warehouses?: WarehouseRef[];
  consignees?: ConsigneeRef[];
  cargoContext?: ItineraryCargoContext;
  isStopLocked?: (stop: DeliveryGroupStopDraft) => boolean;
}): OrderedRouteStopRow[] {
  const applications = params.applications ?? [];
  const warehouses = params.warehouses ?? [];
  const consignees = params.consignees ?? [];
  const allocationEdges = resolveItineraryAllocationEdges(params.cargoContext);
  const isLocked = params.isStopLocked ?? ((stop) => resolveLockState(stop) !== 'FUTURE');
  const typeCounters = { loading: 0, unloading: 0 };

  return getOrderedStops(params.groups).map((entry) => {
    const row = entryToRow(
      entry,
      params.groups,
      applications,
      warehouses,
      consignees,
      allocationEdges,
      typeCounters
    );
    return { ...row, locked: isLocked(entry.stop) };
  });
}

export function orderedRouteStopUids(groups: DeliveryGroupDraft[]): string[] {
  return buildOrderedRouteStops({ groups }).map((r) => r.stopUid);
}
